import React, { useEffect, useMemo, useState } from 'react';

/**
 * Centered city picker for signup: searchable, mobile-first, bounded scroll.
 */
const CityPickerModal = ({
  open,
  onClose,
  cities = [],
  loading = false,
  selectedCity = '',
  onSelect,
  maxPanelHeightPx = null,
}) => {
  const [query, setQuery] = useState('');

  useEffect(() => {
    if (!open) setQuery('');
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onEscape = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onEscape);
    return () => window.removeEventListener('keydown', onEscape);
  }, [open, onClose]);

  const filteredCities = useMemo(() => {
    const q = (query || '').trim().toLowerCase();
    if (!q) return cities;
    return cities.filter((c) => (c || '').toLowerCase().includes(q));
  }, [cities, query]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 bg-black/50"
      onClick={onClose}
      role="presentation"
    >
      <div
        className="bg-white rounded-lg shadow-xl w-full max-w-md max-h-[85vh] flex flex-col"
        style={maxPanelHeightPx ? { maxHeight: `${maxPanelHeightPx}px` } : undefined}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="city-picker-title"
      >
        <div className="p-4 border-b border-gray-200">
          <h3 id="city-picker-title" className="text-lg font-semibold text-gray-900">
            Select your city
          </h3>
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search city..."
            className="mt-3 w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            autoFocus
            aria-label="Search city"
          />
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto p-2">
          {loading && <p className="py-4 text-center text-gray-500 text-sm">Loading cities...</p>}
          {!loading && filteredCities.length === 0 && (
            <p className="py-4 text-center text-gray-500 text-sm">
              {query.trim() ? 'No city matches your search.' : 'No cities available.'}
            </p>
          )}
          {!loading && filteredCities.length > 0 && (
            <ul className="space-y-0.5">
              {filteredCities.map((city) => (
                <li key={city}>
                  <button
                    type="button"
                    onClick={() => {
                      onSelect(city);
                      onClose();
                    }}
                    className={`w-full px-3 py-2.5 text-left rounded-md focus:outline-none ${
                      selectedCity === city ? 'bg-blue-50 text-blue-700' : 'hover:bg-gray-100 focus:bg-gray-100 text-gray-900'
                    }`}
                  >
                    {city}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="p-3 border-t border-gray-200">
          <button
            type="button"
            onClick={onClose}
            className="w-full px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default CityPickerModal;
